import React, { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("Caught by ErrorBoundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
          <div className="max-w-sm">
            <h2 className="text-base font-medium text-foreground mb-1">
              Something went wrong
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              An unexpected error occurred. Please try again.
            </p>

            <div className="flex gap-2 justify-center mb-6">
              <button
                onClick={this.handleReset}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.replace("/")}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-border text-foreground hover:bg-accent transition-colors"
              >
                Go Home
              </button>
            </div>

            <details
              className="w-full p-3 rounded-md border border-border text-left whitespace-pre-wrap text-xs text-muted-foreground overflow-auto"
              open={false}
            >
              <summary className="cursor-pointer text-xs font-medium mb-2">
                Technical details
              </summary>
              {error?.toString()}
              <br />
              {errorInfo?.componentStack}
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
